import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUsers,
  fetchUserById,
  updateUserRole,
  toggleUserBan,
  selectUsers,
  selectUsersPagination,
  selectUsersStatistics,
  selectLoading as selectUserLoading,
  selectErrors as selectUserErrors,
  selectCurrentUser,
} from "../../redux/slices/userManagementSlice";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminUsersChrome from "../users/AdminUsersChrome";
import AdminUsersFiltersRail from "../users/AdminUsersFiltersRail";
import UsersFloorIndex from "../users/floor/UsersFloorIndex";
import UserDetailPanel from "../users/UserDetailPanel";
import UserDetailEmpty from "../users/UserDetailEmpty";
import UserRoleModal from "../users/modals/UserRoleModal";
import UserBanModal from "../users/modals/UserBanModal";
import UserRewardModal from "../users/modals/UserRewardModal";
import {
  applyDeskFilter,
  buildFetchParams,
  countStaffUsers,
  sortUsers,
} from "../users/constants";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../users/admin-users.css";

export default function UsersPage({ theme }) {
  const dispatch = useDispatch();

  const users = useSelector(selectUsers);
  const userLoading = useSelector(selectUserLoading);
  const userErrors = useSelector(selectUserErrors);
  const usersPagination = useSelector(selectUsersPagination);
  const usersStatistics = useSelector(selectUsersStatistics);
  const currentUser = useSelector(selectCurrentUser);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [desk, setDesk] = useState("all");
  const [sortKey, setSortKey] = useState("newest");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [usersPerPage] = useState(10);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [actionUser, setActionUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchParams = useMemo(
    () => buildFetchParams({ desk, search, roleFilter, statusFilter, page: 1, limit: 10 }),
    [desk, search, roleFilter, statusFilter]
  );

  const displayUsers = useMemo(() => {
    const list = applyDeskFilter(users || [], desk);
    return sortUsers(list, sortKey);
  }, [users, desk, sortKey]);

  const roleModalUser = useMemo(() => {
    if (!actionUser) return null;
    if (currentUser?._id === actionUser._id) {
      return { ...actionUser, ...currentUser };
    }
    return actionUser;
  }, [actionUser, currentUser]);

  const staffCount = useMemo(() => countStaffUsers(users), [users]);
  const bannedCount = usersStatistics?.bannedCount || 0;

  const activeFilterCount = [
    desk !== "all",
    statusFilter !== "all",
    roleFilter !== "all",
    sortKey !== "newest",
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setDesk("all");
    setStatusFilter("all");
    setRoleFilter("all");
    setSortKey("newest");
    setSearch("");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchAllUsers({ ...fetchParams, refresh: true }));
    setRefreshing(false);
  };

  const loadUsersPage = (page) => {
    dispatch(fetchAllUsers(buildFetchParams({ desk, search, roleFilter, statusFilter, page, limit: 10 })));
  };

  // Server-side pagination values
  const totalPages = usersPagination?.totalPages || 1;
  const currentPageServer = usersPagination?.currentPage || 1;
  const pageSizeServer = usersPagination?.usersPerPage || usersPerPage;
  const totalUsersServer = usersPagination?.totalUsers || displayUsers.length;
  const startIndex = (currentPageServer - 1) * pageSizeServer;
  const endIndex = startIndex + pageSizeServer;
  const currentUsers = displayUsers;
  const totalUsers = usersStatistics?.totalUsers || totalUsersServer || 0;
  const activeCount = usersStatistics?.activeCount || 0;

  useEffect(() => {
    dispatch(fetchAllUsers(fetchParams));
  }, [dispatch, fetchParams]);

  useEffect(() => {
    if (users?.length > 0 && !selectedUser) {
      setSelectedUser(users[0]);
      dispatch(fetchUserById(users[0]._id));
    }
  }, [users, selectedUser, dispatch]);

  const railProps = {
    desk,
    status: statusFilter,
    role: roleFilter,
    sortKey,
    staffCount,
    bannedCount,
    onDeskChange: setDesk,
    onStatusChange: setStatusFilter,
    onRoleChange: setRoleFilter,
    onSortChange: setSortKey,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const handleSelectUser = useCallback(
    (user) => {
      if (!user?._id) return;
      setSelectedUser(user);
      dispatch(fetchUserById(user._id));
    },
    [dispatch]
  );

  // Handle role update with Redux
  const handleRoleUpdate = async (userId, newRole, ambassadorFields = {}) => {
    setIsLoading(true);
    try {
      const result = await dispatch(
        updateUserRole({ userId, newRole, ...ambassadorFields })
      ).unwrap();
      if (result.success) {
        const updatedUser = {
          ...selectedUser,
          ...result.user,
          role: newRole,
        };
        setSelectedUser(updatedUser);
        
        setShowRoleModal(false);
        setActionUser(null);
      }
    } catch (error) {
      // You could add error handling here (show toast, etc.)
    } finally {
      setIsLoading(false);
    }
  };

  // Handle ban toggle with Redux
  const handleBanToggle = async (userId, banReason = null) => {
    setIsLoading(true);
    try {
      // Dispatch the Redux thunk to toggle user ban
      const result = await dispatch(toggleUserBan({ userId, banReason })).unwrap();
      if (result.success) {
        // Update the selected user with new ban status
        const updatedUser = {
          ...selectedUser, 
          isBanned: result.user.isBanned,
          banReason: result.user.banReason,
          bannedAt: result.user.bannedAt,
          bannedBy: result.user.bannedBy
        };
        setSelectedUser(updatedUser);
        
        // Close modal and reset
        setShowBanModal(false);
        setActionUser(null);
      }
    } catch (error) {
      // You could add error handling here (show toast, etc.)
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <div className="am-page au-page">
        <div className="am-aurora" aria-hidden>
          <span className="am-aurora-a au-aurora" />
        </div>

        <AdminUsersChrome
          search={search}
          onSearchChange={setSearch}
          loading={userLoading.users || refreshing}
          onRefresh={handleRefresh}
          filtersOpen={filtersOpen}
          onFiltersToggle={() => setFiltersOpen((v) => !v)}
          activeFilterCount={activeFilterCount}
          totalUsers={totalUsers}
          activeCount={activeCount}
          bannedCount={bannedCount}
        />

        <AdminDesk
          sectionKey="users"
          sidebarLabel="User filters"
          sidebar={<AdminUsersFiltersRail {...railProps} />}
        >
          <div className="am-board am-board-floor">
            {userErrors.users ? (
              <div className="au-index-error" role="alert">
                {userErrors.users}
              </div>
            ) : null}
            <AdminFloorGrid
              sectionKey="users"
              index={
                <UsersFloorIndex
                  users={currentUsers}
                  selectedId={selectedUser?._id}
                  onSelect={handleSelectUser}
                  desk={desk}
                  totalLoaded={totalUsersServer}
                  loading={userLoading.users}
                  pagination={{
                    totalPages,
                    currentPage: currentPageServer,
                    startIndex,
                    endIndex,
                    totalUsers: totalUsersServer,
                    onPageChange: loadUsersPage,
                  }}
                />
              }
              detail={
                <div className="au-detail-wrap">
                  {selectedUser ? (
                    <UserDetailPanel
                      user={selectedUser}
                      detailUser={currentUser}
                      onEditRole={() => {
                        setActionUser(selectedUser);
                        if (selectedUser?._id) dispatch(fetchUserById(selectedUser._id));
                        setShowRoleModal(true);
                      }}
                      onReward={() => {
                        setActionUser(selectedUser);
                        setShowRewardModal(true);
                      }}
                      onBan={() => {
                        setActionUser(selectedUser);
                        setShowBanModal(true);
                      }}
                    />
                  ) : (
                    <UserDetailEmpty />
                  )}
                </div>
              }
            />
          </div>
        </AdminDesk>

        {filtersOpen ? (
          <div className="am-filters-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
            <div
              className="am-filters-drawer"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="User filters"
            >
              <AdminUsersFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
            </div>
          </div>
        ) : null}
      </div>

      <UserRoleModal
        open={showRoleModal}
        user={roleModalUser}
        loading={isLoading}
        onClose={() => {
          setShowRoleModal(false);
          setActionUser(null);
        }}
        onConfirm={handleRoleUpdate}
      />

      <UserBanModal
        open={showBanModal}
        user={actionUser}
        loading={isLoading}
        onClose={() => {
          setShowBanModal(false);
          setActionUser(null);
        }}
        onConfirm={handleBanToggle}
      />

      <UserRewardModal
        open={showRewardModal}
        user={actionUser}
        theme={theme}
        onClose={() => {
          setShowRewardModal(false);
          setActionUser(null);
        }}
        onSuccess={() => {
          if (actionUser?._id) dispatch(fetchUserById(actionUser._id));
        }}
      />
    </>
  );
}
